import React, { useState, useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Modal } from '../modal'
import { Button } from '../button'
import { expenseApi } from 'src/services/expenses/api'
import { retencionTypeEnum, RetencionTypeValues } from 'src/services/expenses/models'
import { useToast } from '../toast'
import { extractErrorMessage } from 'src/utils/errors'
import { cn } from 'src/utils/style'
import { useAsyncJobStore } from 'src/stores/async-jobs'
import { taskTypeEnum, buildOptimisticJob } from 'src/services/async-jobs/models'

interface GenerateCuentaDeCobroModalProps {
  isOpen: boolean
  onClose: () => void
  expenseId: string
  vendorName: string
  vendorWithholdingRate?: number | null
  expenseAmount: number
  expenseDate: string
  expenseCurrency: string
}

export const GenerateCuentaDeCobroModal: React.FC<GenerateCuentaDeCobroModalProps> = ({
  isOpen,
  onClose,
  expenseId,
  vendorName,
  vendorWithholdingRate,
  expenseAmount,
  expenseDate,
  expenseCurrency,
}) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const restoreActive = useAsyncJobStore((s) => s.actions.restoreActive)

  const [retencionType, setRetencionType] = useState<RetencionTypeValues>(
    retencionTypeEnum.PERCENTAGE,
  )
  const [retencionValue, setRetencionValue] = useState<string>(
    vendorWithholdingRate != null ? String(vendorWithholdingRate) : '',
  )

  const parsedValue = parseFloat(retencionValue) || 0

  const calculation = useMemo(() => {
    const gross = expenseAmount
    let retencion = 0
    if (retencionType === retencionTypeEnum.PERCENTAGE) {
      retencion = gross * (parsedValue / 100)
    } else {
      retencion = parsedValue
    }
    const net = gross - retencion
    return { gross, retencion, net }
  }, [expenseAmount, retencionType, parsedValue])

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('es-CO', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(value))

  const { mutate: generate, isPending } = useMutation({
    mutationFn: () =>
      expenseApi.csc.generateCuentaDeCobro({
        id: expenseId,
        retencionType: retencionType,
        retencionValue: parsedValue,
      }),
    onSuccess: (data) => {
      // Light up the expense's cuenta spinner immediately; ws/poll supersede it.
      restoreActive(
        buildOptimisticJob({
          id: data.asyncJobId,
          taskType: taskTypeEnum.CUENTA_DE_COBRO_GENERATION,
          entityId: expenseId,
          entityType: 'Expense',
          label: t('asyncJobs.generatingCuentaDeCobro'),
        }),
      )
      toast({
        description: t('cuentaDeCobro.generatingToast'),
        type: 'default',
      })
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      onClose()
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: extractErrorMessage(error),
        type: 'error',
      })
    },
  })

  const handleSubmit = () => {
    generate()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('cuentaDeCobro.title')} size="md">
      <div className="space-y-4">
        {/* Summary */}
        <div className="rounded-md bg-surface-hover p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-text-secondary">{t('cuentaDeCobro.vendor')}</span>
            <span className="font-medium">{vendorName}</span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-text-secondary">{t('cuentaDeCobro.amount')}</span>
            <span className="font-medium">
              ${formatCurrency(expenseAmount)} {expenseCurrency}
            </span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-text-secondary">{t('cuentaDeCobro.date')}</span>
            <span className="font-medium">{expenseDate}</span>
          </div>
        </div>

        {/* Retencion Type Toggle */}
        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">
            {t('cuentaDeCobro.retencionType')}
          </label>
          <div className="flex rounded-md border border-border">
            <button
              type="button"
              className={cn(
                'flex-1 rounded-l-md px-3 py-2 text-sm transition-colors',
                retencionType === retencionTypeEnum.PERCENTAGE
                  ? 'bg-primary text-white'
                  : 'bg-surface text-text-secondary hover:bg-surface-hover',
              )}
              onClick={() => setRetencionType(retencionTypeEnum.PERCENTAGE)}
            >
              {t('cuentaDeCobro.percentageOfAmount')}
            </button>
            <button
              type="button"
              className={cn(
                'flex-1 rounded-r-md px-3 py-2 text-sm transition-colors',
                retencionType === retencionTypeEnum.FIXED
                  ? 'bg-primary text-white'
                  : 'bg-surface text-text-secondary hover:bg-surface-hover',
              )}
              onClick={() => setRetencionType(retencionTypeEnum.FIXED)}
            >
              {t('cuentaDeCobro.fixedCopValue')}
            </button>
          </div>
        </div>

        {/* Retencion Value Input */}
        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">
            {retencionType === retencionTypeEnum.PERCENTAGE
              ? t('cuentaDeCobro.retencionPercentage')
              : t('cuentaDeCobro.retencionAmount')}
          </label>
          <div className="relative">
            <input
              type="number"
              step="0.01"
              min="0"
              value={retencionValue}
              onChange={(e) => setRetencionValue(e.target.value)}
              className="w-full rounded-md border border-border bg-surface p-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-border-strong"
              placeholder={
                retencionType === retencionTypeEnum.PERCENTAGE
                  ? t('cuentaDeCobro.percentagePlaceholder')
                  : t('cuentaDeCobro.fixedPlaceholder')
              }
            />
            {retencionType === retencionTypeEnum.PERCENTAGE && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary">
                %
              </span>
            )}
          </div>
        </div>

        {/* Live Calculation Preview */}
        <div className="rounded-md border border-border-strong bg-surface-hover p-3 text-sm">
          <div className="flex justify-between">
            <span>{t('cuentaDeCobro.gross')}</span>
            <span className="font-medium">
              ${formatCurrency(calculation.gross)} {expenseCurrency}
            </span>
          </div>
          <div className="mt-1 flex justify-between">
            <span>{t('cuentaDeCobro.retencion')}</span>
            <span className="font-medium text-error">
              -${formatCurrency(calculation.retencion)} {expenseCurrency}
            </span>
          </div>
          <div className="mt-1 flex justify-between border-t border-border pt-1">
            <span className="font-semibold">{t('cuentaDeCobro.net')}</span>
            <span className="font-semibold">
              ${formatCurrency(calculation.net)} {expenseCurrency}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isPending}>
            {isPending ? t('common.generating') : t('common.generate')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
